// ─── Public Status Page — the data loop ──────────────────────────────
//
// POLLING, NOT SSE — on purpose.
//
// A status page's traffic profile is the exact inverse of the rest of PCP: it
// is idle almost all the time, and then, during the ten minutes when something
// is actually broken, every customer and every internal team opens it at once.
// SSE would hold ONE open connection per viewer for as long as that tab stays
// open. A single Fastify process holding thousands of long-lived response
// streams is precisely the failure mode this page exists to survive — we would
// be adding load to a struggling API at the one moment it must not be touched,
// and the first thing to break would be the page telling people what broke.
//
// Polling degrades instead: each client makes one short, cacheable, ETag'd GET
// on an interval, the server can shed it, a CDN can absorb it, and when the API
// is gone entirely the same loop falls through to the static snapshot without
// any of this code knowing.
//
// The loop's four rules:
//   1. Poll faster while something is wrong (15 s) than while all is well (60 s).
//   2. Jitter every delay ±15 %, so a thousand tabs opened during the same
//      outage announcement do not re-align into a synchronised herd.
//   3. Back off exponentially, to a 5-minute ceiling, on consecutive TOTAL
//      failures. A live failure that succeeds from the SNAPSHOT is not a failure
//      for this purpose — it only flips `source`, and we keep polling normally
//      so the page recovers the moment the API returns.
//   4. NEVER clear data on a failed refetch. The last good render stays on
//      screen; only `source`, `fetchedAt` and `failures` move.

import { useCallback, useEffect, useRef, useState } from 'react';
import type { PublicStatusSummary } from '@pcp/shared';
import { fetchStatus, type StatusSource } from './statusApi';

/** All-clear cadence. */
export const POLL_IDLE_MS = 60_000;
/** Something is degraded / an incident is open — the page is being watched. */
export const POLL_ACTIVE_MS = 15_000;
/** First backoff step after a total failure. */
const BACKOFF_BASE_MS = 15_000;
/** Backoff ceiling — we never stop retrying, we just stop hammering. */
const BACKOFF_MAX_MS = 5 * 60_000;
/** ±15 % — enough to smear a herd across a whole interval. */
const JITTER = 0.15;
/** A tab-back within this window reuses the data we already have. */
const REFOCUS_MIN_AGE_MS = POLL_ACTIVE_MS;

export interface UseStatusDataResult {
  data: PublicStatusSummary | null;
  source: StatusSource;
  /** epoch ms of the last SUCCESSFUL load, or null before the first one. */
  fetchedAt: number | null;
  status: 'loading' | 'ready' | 'error';
  /** Consecutive total failures (live AND snapshot). 0 once anything succeeds. */
  failures: number;
  /** Force an immediate reload and restart the schedule. */
  refresh: () => void;
}

interface InternalState {
  data: PublicStatusSummary | null;
  source: StatusSource;
  fetchedAt: number | null;
  status: 'loading' | 'ready' | 'error';
  failures: number;
}

const INITIAL: InternalState = {
  data: null,
  source: 'live',
  fetchedAt: null,
  status: 'loading',
  failures: 0,
};

/** Is anything worth watching closely right now? */
function isActive(data: PublicStatusSummary | null): boolean {
  if (!data) return false;
  if (data.overall !== 'available') return true;
  return data.incidents.some((incident) => incident.state === 'open');
}

function withJitter(ms: number): number {
  const spread = ms * JITTER;
  return Math.max(1_000, Math.round(ms - spread + Math.random() * spread * 2));
}

function nextDelay(failures: number, active: boolean): number {
  if (failures > 0) {
    const backoff = BACKOFF_BASE_MS * 2 ** Math.min(failures - 1, 10);
    return withJitter(Math.min(backoff, BACKOFF_MAX_MS));
  }
  return withJitter(active ? POLL_ACTIVE_MS : POLL_IDLE_MS);
}

export function useStatusData(audience: string | null): UseStatusDataResult {
  const [state, setState] = useState<InternalState>(INITIAL);
  // The whole loop lives inside ONE effect keyed on `audience`, so every timer,
  // counter and listener is created and torn down together — no stale closure
  // can survive an audience switch, and there is no cross-render ref dance.
  const refreshRef = useRef<() => void>(() => undefined);

  useEffect(() => {
    let cancelled = false;
    let timer: number | null = null;
    let inflight = false;
    let failures = 0;
    let active = false;
    let lastSuccessAt = 0;

    // A new audience means the payload on screen belongs to a different page.
    setState(INITIAL);

    const clearTimer = () => {
      if (timer !== null) {
        window.clearTimeout(timer);
        timer = null;
      }
    };

    const schedule = () => {
      clearTimer();
      // A hidden tab costs the API nothing. visibilitychange resumes it.
      if (cancelled || document.hidden) return;
      timer = window.setTimeout(() => {
        void run();
      }, nextDelay(failures, active));
    };

    const run = async (): Promise<void> => {
      if (cancelled || inflight) return;
      inflight = true;
      clearTimer();
      try {
        const result = await fetchStatus(audience);
        if (cancelled) return;
        failures = 0;
        active = isActive(result.data);
        lastSuccessAt = result.fetchedAt;
        setState({
          data: result.data,
          source: result.source,
          fetchedAt: result.fetchedAt,
          status: 'ready',
          failures: 0,
        });
      } catch {
        if (cancelled) return;
        failures += 1;
        // Rule 4: keep whatever is already rendered. Only a page that never
        // managed a single load is allowed to show the failure card.
        setState((prev) => ({
          ...prev,
          status: prev.data ? 'ready' : 'error',
          failures,
        }));
      } finally {
        inflight = false;
        if (!cancelled) schedule();
      }
    };

    refreshRef.current = () => {
      failures = 0; // an explicit user action resets the backoff
      void run();
    };

    const onVisibilityChange = () => {
      if (document.hidden) {
        clearTimer();
        return;
      }
      // Coming back to the tab: refetch if what we hold could plausibly be
      // stale, otherwise just restart the timer.
      if (Date.now() - lastSuccessAt >= REFOCUS_MIN_AGE_MS) void run();
      else schedule();
    };

    // Reconnecting is the one moment a retry is guaranteed to be worth it.
    const onOnline = () => {
      failures = 0;
      void run();
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('online', onOnline);
    void run();

    return () => {
      cancelled = true;
      clearTimer();
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('online', onOnline);
    };
  }, [audience]);

  const refresh = useCallback(() => {
    refreshRef.current();
  }, []);

  return {
    data: state.data,
    source: state.source,
    fetchedAt: state.fetchedAt,
    status: state.status,
    failures: state.failures,
    refresh,
  };
}
